import { getData } from "../../helpers/url-request";
import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import CountUp from "react-countup";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import { Link } from "react-router-dom";
import { ErrorNotify } from "../../shared/Alert";
import {
  getCompanyList,
  getUsersList,
} from "../../helpers/backend_helper";
import "./SystemActivityDashboard.scss";

const statusNames = ["Not started", "In progress", "Under review", "Completed"];

const responseData = (response) =>
  response?.status === false || !Array.isArray(response?.data)
    ? []
    : response.data;

const metricCards = (metrics) => [
  {
    label: "Total Users",
    value: metrics.users,
    note: "Active platform users",
    icon: "bx-user",
    tone: "violet",
  },
  {
    label: "Total Firms",
    value: metrics.firms,
    note: "Registered firms",
    icon: "bx-building-house",
    tone: "blue",
  },
  {
    label: "Active Engagements",
    value: metrics.activeEngagements,
    note: "Currently in progress",
    icon: "bx-briefcase-alt-2",
    tone: "green",
  },
  {
    label: "Inspection Cycles",
    value: metrics.inspections,
    note: "Across all firms",
    icon: "bx-clipboard",
    tone: "orange",
  },
  {
    label: "Open Issues",
    value: metrics.openIssues,
    note: "Pending review tasks",
    icon: "bx-error-circle",
    tone: "red",
  },
];

const SystemActivityDashboard = () => {
  const [data, setData] = useState({
    users: [],
    firms: [],
    summary: {},
  });
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      const [usersResult, firmsResult, summaryResult] = await Promise.all([
        getUsersList(), getCompanyList(), getData("/system-administration/summary"),
      ]);
      if (!summaryResult?.status) throw new Error("Administration summary unavailable");
      setData({ users: responseData(usersResult), firms: responseData(firmsResult), summary: summaryResult.data });
    } catch (error) {
      console.error(error);
      ErrorNotify("Unable to load the application dashboard data.");
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  const metrics = useMemo(
    () => ({
      users: data.users.length,
      firms: data.firms.length,
      activeEngagements: data.summary.activeEngagements || 0,
      inspections: data.summary.inspectionCycles || 0,
      openIssues: data.summary.openIssues || 0,
    }),
    [data]
  );

  const engagementStatus = useMemo(
    () =>
      statusNames.map((name) => ({
        name,
        value: (data.summary.engagementStatus || []).filter((item) => {
          const status = String(item.name || "Not started").toLowerCase();
          if (name === "Not started") return /not started|new|draft/.test(status);
          if (name === "In progress") return /progress|active/.test(status);
          if (name === "Under review") return /review/.test(status);
          return /complete|approved|closed/.test(status);
        }).reduce((total, item) => total + item.value, 0),
      })),
    [data.summary]
  );

  const userSummary = useMemo(() => {
    const roles = data.users.reduce((summary, user) => {
      const name =
        user?.role?.name || user?.roleName || user?.designation || "Firm users";
      summary[name] = (summary[name] || 0) + 1;
      return summary;
    }, {});
    const entries = Object.entries(roles).map(([name, value]) => ({
      name,
      value,
    }));
    return entries.length ? entries.slice(0, 4) : [{ name: "Users", value: 0 }];
  }, [data.users]);

  const trend = useMemo(() => {
    const days = Array.from({ length: 6 }, (_, index) => {
      const date = new Date();
      date.setDate(date.getDate() - (5 - index) * 7);
      return date;
    });

    const bucket = (items, dateField) =>
      days.map(
        (day, index) =>
          items.filter((item) => {
            const date = new Date(
              item?.[dateField] || item?.createdOn || item?.updatedOn
            );
            const next = new Date(day);
            next.setDate(next.getDate() + 7);
            return !Number.isNaN(date.valueOf()) && date >= day && date < next;
          }).reduce((total, item) => total + (item.count || 1), 0)
      );

    return {
      categories: days.map((day) =>
        day.toLocaleDateString(undefined, { month: "short", day: "numeric" })
      ),
      series: [
        { name: "Users", data: bucket(data.users, "createdOn") },
        { name: "Engagements", data: bucket(data.summary.engagementTrend || [], "createdOn") },
        {
          name: "Inspection cycles",
          data: bucket(data.summary.inspectionTrend || [], "createdOn"),
        },
      ],
    };
  }, [data]);

  const recentActivity = useMemo(
    () =>
      [
        ...data.users.slice(0, 2).map((user) => ({
          icon: "bx-user-plus",
          tone: "green",
          title: `User ${user.fullName || user.name || "account"} registered`,
          detail: user.emailId || user.loginName || "Platform user",
        })),
        ...data.firms.slice(0, 1).map((firm) => ({
          icon: "bx-building-house",
          tone: "blue",
          title: `Firm ${firm.name || "account"} added`,
          detail: firm.emailId || "Firm administration",
        })),

      ].slice(0, 5),
    [data]
  );

  const cards = metricCards(metrics);
  const donutColors = ["#7c4dff", "#2563eb", "#46bf78", "#fb8b24"];

  return (
    <div className="app-admin-dashboard">
      <Container fluid>
        <header className="app-admin-dashboard__header">
          <div>
            <h1 className="piers-page-title">Application Admin Dashboard</h1>
            <p>Overview of system health, users and activity</p>
          </div>
          <button
            className="app-admin-dashboard__refresh piers-refresh-button"
            type="button"
            onClick={refreshData}
            disabled={isRefreshing}
          >
            <i
              className={`bx bx-refresh ${isRefreshing ? "is-spinning" : ""}`}
            ></i>
            {isRefreshing ? "Refreshing" : "Refresh"}
          </button>
        </header>

        <Row className="g-3 app-admin-dashboard__metrics">
          {cards.map((card) => (
            <Col key={card.label} xl={2} lg={4} sm={6}>
              <Card className="admin-metric-card h-100">
                <CardBody>
                  <span
                    className={`admin-metric-card__icon admin-metric-card__icon--${card.tone}`}
                  >
                    <i className={`bx ${card.icon}`}></i>
                  </span>
                  <div>
                    <span className="admin-metric-card__label">
                      {card.label}
                    </span>
                    <strong>
                      <CountUp
                        start={0}
                        end={card.value}
                        duration={0.7}
                        separator=","
                      />
                    </strong>
                    <small>{card.note}</small>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
          <Col xl={2} lg={4} sm={6}>
            <Card className="admin-metric-card admin-metric-card--health h-100">
              <CardBody>
                <span className="admin-metric-card__icon admin-metric-card__icon--violet">
                  <i className="bx bx-shield-quarter"></i>
                </span>
                <div>
                  <span className="admin-metric-card__label">
                    System Health
                  </span>
                  <strong className="admin-metric-card__healthy">
                    Healthy
                  </strong>
                  <small>Services responding</small>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mt-1">
          <Col xl={5}>
            <Card className="admin-dashboard-card h-100">
              <CardBody>
                <h2>System Activity Overview</h2>
                <ReactApexChart
                  type="area"
                  height={245}
                  series={trend.series}
                  options={{
                    chart: { toolbar: { show: false }, fontFamily: "inherit" },
                    colors: ["#2563eb", "#46bf78", "#8b5cf6"],
                    dataLabels: { enabled: false },
                    stroke: { curve: "smooth", width: 2.5 },
                    fill: { type: "solid", opacity: 0.08 },
                    grid: { borderColor: "#e8edf5" },
                    legend: {
                      position: "top",
                      horizontalAlign: "left",
                      fontSize: "12px",
                    },
                    xaxis: {
                      categories: trend.categories,
                      labels: {
                        style: { colors: "#607092", fontSize: "12px" },
                      },
                    },
                    yaxis: {
                      labels: {
                        style: { colors: "#607092", fontSize: "12px" },
                      },
                    },
                    tooltip: { theme: "light" },
                  }}
                />
              </CardBody>
            </Card>
          </Col>
          <Col xl={4}>
            <Card className="admin-dashboard-card h-100">
              <CardBody>
                <h2>User Summary</h2>
                <div className="admin-user-summary">
                  <ReactApexChart
                    type="donut"
                    width={190}
                    height={225}
                    series={userSummary.map((item) => item.value)}
                    options={{
                      chart: { fontFamily: "inherit" },
                      labels: userSummary.map((item) => item.name),
                      colors: donutColors,
                      legend: { show: false },
                      dataLabels: { enabled: false },
                      stroke: { width: 0 },
                      plotOptions: {
                        pie: {
                          donut: {
                            size: "68%",
                            labels: {
                              show: true,
                              total: {
                                show: true,
                                label: "Total Users",
                                formatter: () => String(metrics.users),
                              },
                            },
                          },
                        },
                      },
                    }}
                  />
                  <div className="admin-user-summary__legend">
                    {userSummary.map((item, index) => (
                      <div key={item.name}>
                        <span
                          style={{ backgroundColor: donutColors[index] }}
                        ></span>
                        <label>{item.name}</label>
                        <b>{item.value}</b>
                      </div>
                    ))}
                  </div>
                </div>
                <Link className="admin-text-link" to="/settings/syssecurity">
                  Manage users <i className="bx bx-right-arrow-alt"></i>
                </Link>
              </CardBody>
            </Card>
          </Col>
          <Col xl={3}>
            <Card className="admin-dashboard-card h-100">
              <CardBody>
                <div className="admin-card-heading">
                  <h2>Recent System Activities</h2>
                  <Link to="/settings/syssecurity">View users</Link>
                </div>
                <div className="admin-activity-list">
                  {recentActivity.length ? (
                    recentActivity.map((activity, index) => (
                      <div key={`${activity.title}-${index}`}>
                        <span
                          className={`admin-activity-list__icon admin-activity-list__icon--${activity.tone}`}
                        >
                          <i className={`bx ${activity.icon}`}></i>
                        </span>
                        <p>
                          <b>{activity.title}</b>
                          <small>{activity.detail}</small>
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="admin-empty-state">No recent activity.</p>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mt-1">
          <Col xl={4}>
            <Card className="admin-dashboard-card h-100">
              <CardBody>
                <div className="admin-card-heading">
                  <h2>Engagement Status</h2>

                </div>
                <div className="admin-status">
                  <ReactApexChart
                    type="donut"
                    width={185}
                    height={210}
                    series={engagementStatus.map((item) => item.value)}
                    options={{
                      chart: { fontFamily: "inherit" },
                      labels: statusNames,
                      colors: ["#aab4c4", "#2877e8", "#fb9228", "#50bd7c"],
                      legend: { show: false },
                      dataLabels: { enabled: false },
                      stroke: { width: 0 },
                      plotOptions: {
                        pie: {
                          donut: {
                            size: "67%",
                            labels: {
                              show: true,
                              total: {
                                show: true,
                                label: "Total",
                                formatter: () =>
                                  String(engagementStatus.reduce((total, item) => total + item.value, 0)),
                              },
                            },
                          },
                        },
                      },
                    }}
                  />
                  <div className="admin-status__legend">
                    {engagementStatus.map((item, index) => (
                      <div key={item.name}>
                        <span
                          className={`admin-status__dot admin-status__dot--${index}`}
                        ></span>
                        <label>{item.name}</label>
                        <b>{item.value}</b>
                      </div>
                    ))}
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl={3}>
            <Card className="admin-dashboard-card h-100">
              <CardBody>
                <div className="admin-card-heading">
                  <h2>System Health</h2>
                  <span className="admin-health-badge">Healthy</span>
                </div>
                <div className="admin-health-list">
                  {[
                    "Application server",
                    "Database server",
                    "File storage",
                    "Email service",
                    "Backup service",
                  ].map((service) => (
                    <div key={service}>
                      <span>{service}</span>
                      <b>
                        <i className="bx bx-check"></i> Healthy
                      </b>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Card className="admin-dashboard-card admin-quick-actions mt-3">
          <CardBody>
            <h2>Quick Actions</h2>
            <div>
              <Link to="/settings/syssecurity">
                <i className="bx bx-user-plus"></i>Add New User
              </Link>
              <Link to="/firm/list">
                <i className="bx bx-building-house"></i>Add New Firm
              </Link>

              <Link to="/settings/syssecurity">
                <i className="bx bx-shield-quarter"></i>Manage Roles
              </Link>

              <Link to="/system/masters">
                <i className="bx bx-cog"></i>System Settings
              </Link>
            </div>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default SystemActivityDashboard;
